// Nur der Typ, kein Wert: compare.ts bindet umgekehrt die Typen dieser
// Datei ein, und ein gegenseitiger Import zur Laufzeit wäre ein Zirkel.
import type { Snapshot } from "./compare.js";

declare global {
    interface ToolsConfig {
        hausinventar?: {
            apiUrl: string;
            mcpApiUrl: string;
            restNonce: string;
            userName: string;
            logoutUrl: string;
            /**
             * Darf der gesamte Bestand gelöscht werden? Wird serverseitig
             * gesetzt und gilt nur für einen namentlichen Zugang auf einer
             * `.test`-Domain (siehe InventoryApp::mayResetEverything).
             */
            canReset?: boolean;
        };
    }
}

export interface GalleryImage {
    ID: number;
    url: string;
    filename: string;
    /** Maße des Originals (aus media_details). */
    width?: number;
    height?: number;
    sizes?: { thumbnail?: string; medium?: string; large?: string };
}

export interface Room {
    id: number;
    title: string;
    /** Reihenfolge (WP-Kernfeld "menu_order"), per Drag & Drop sortierbar. */
    order: number;
    beschreibung: string;
    fotos: GalleryImage[];
}

/** Dringlichkeit eines Import-Hinweises, absteigend. */
export type NoteLevel = "kritisch" | "warnung" | "hinweis";

/**
 * Ein Hinweis aus dem Foto-Import.
 *
 * Abzugrenzen von `importLog` an Position und Gegenstand: Das protokolliert,
 * wie **ein** Datensatz zustande kam. Hier steht, was darüber hinausgeht —
 * offene Fragen, Warnungen und Lücken, die mehrere Datensätze oder den ganzen
 * Lauf betreffen und sonst mit dem Chatverlauf verschwinden würden.
 */
export interface Note {
    id: number;
    title: string;
    order: number;
    level: NoteLevel;
    text: string;
    erledigt: boolean;
    /** Herkunftsbild, falls der Hinweis an einem bestimmten Foto entstand. */
    imageKey: string;
    /** Verweise auf die betroffenen Datensätze; 0 = nicht gesetzt. */
    raum: number;
    position: number;
    item: number;
}

/** Eine beschriebene Sache/Gruppe eines Raums, z. B. "Bücherregal Wohnzimmer". */
export interface Position {
    id: number;
    title: string;
    /** Rein manuell vergebene Nummer. */
    nummer: number;
    raum: number;
    beschreibung: string;
    /**
     * Dateiname des Import-Fotos ohne Endung — die einzige Spur, über die sich
     * eine Position ihrer Quelldatei zuordnen lässt. Wird nie geschrieben,
     * sondern nur gelesen (Duplikatserkennung beim Import, Instanzvergleich).
     */
    imageKey: string;
    /**
     * Protokoll des automatischen Foto-Imports (Herkunftsdatei, Quelle von
     * Nummer und Raum, getroffene Entscheidungen). Nur zur Nachvollziehbarkeit
     * — anders als `beschreibung` nicht Teil des Versicherungs-Exports.
     */
    importLog: string;
    fotos: GalleryImage[];
}

/**
 * Rechteck auf einem Foto der Position, das zeigt, wo der Gegenstand liegt.
 * Koordinaten sind relativ (0–1) zur Bildgröße, damit sie unabhängig von
 * Auflösung und Thumbnail-Größe gültig bleiben. `media` verweist auf die
 * Attachment-ID — eine Position kann mehrere Fotos haben.
 */
export interface ItemCrop {
    media: number;
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * Ein Vergleichsangebot aus einem Online-Shop.
 *
 * Ein Gegenstand kann mehrere davon haben: Der Wiederbeschaffungswert lässt
 * sich nur belegen, wenn nachvollziehbar ist, welches Produkt zu welchem
 * Preis und zu welchem Zeitpunkt herangezogen wurde. Preise ändern sich —
 * ohne Abrufdatum ist ein Link Monate später wertlos.
 */
export interface ShopLink {
    url: string;
    /** Preis im Shop zum Zeitpunkt des Abrufs, in Euro. 0 = nicht notiert. */
    preis: number;
    /** Datum des Abrufs (ISO "Y-m-d"), leer wenn nicht notiert. */
    datum: string;
}

/**
 * Verweis auf einen Gegenstand, mit dem dieser hier verwechselt sein könnte.
 *
 * Die Referenz ist bewusst so genau wie möglich: Mit Gegenstands- und
 * Positions-ID lässt sich der Verdacht in zwei Klicks nachprüfen, statt ihn
 * aus einer Notiz wie "steht wohl auch in einem anderen Raum" rekonstruieren
 * zu müssen. `item` darf 0 sein, wenn beim Import nur die Position bekannt war
 * (etwa weil dort noch gar keine Zeile angelegt wurde).
 */
export interface DuplicateRef {
    /** ID des anderen Gegenstands, 0 wenn unbekannt. */
    item: number;
    /** ID der Position, in der der andere Gegenstand steht. */
    position: number;
    /** Warum der Verdacht besteht — die eigentliche Prüfgrundlage. */
    note: string;
}

/**
 * Bearbeitungsstand des Duplikat-Verdachts.
 *
 * `""` heißt "kein Verdacht" und ist der Normalfall. `bestaetigt` nimmt die
 * Zeile aus der Versicherungssumme heraus, ohne sie zu löschen — der Beleg,
 * dass geprüft wurde, bleibt so erhalten.
 */
export type DuplicateStatus = "" | "moeglich" | "bestaetigt" | "kein_duplikat";

/** Ein einzelner bewerteter Gegenstand innerhalb einer Position. */
export interface Item {
    id: number;
    title: string;
    /** Reihenfolge innerhalb der Position (WP-Kernfeld "menu_order"), per Drag & Drop sortierbar. */
    order: number;
    position: number;
    anzahl: number;
    /** Geschätztes Alter des Gegenstands, z. B. "ca. 3 Jahre". Immer eine Schätzung. */
    alter: string;
    wert: number;
    /**
     * Versandkosten der Wiederbeschaffung, **je Bestellung** — nicht je Stück.
     *
     * Sie fließen bewusst weder in `wert` noch in eine Summe ein: Der
     * Wiederbeschaffungswert ist der Preis der Sache, und ob die Versicherung
     * Versandkosten überhaupt erstattet, entscheidet sie und nicht diese
     * Aufstellung. Erfasst werden sie trotzdem, weil sie sich später nicht mehr
     * rekonstruieren lassen, wenn der Beleg weg ist.
     */
    versandkosten: number;
    datum: string;
    quelle: string;
    /** Vergleichsangebote, auf denen `wert` beruht — sortiert wie erfasst. */
    shopLinks: ShopLink[];
    /**
     * Warum dieser Betrag angemessen ist, in eigenen Worten des Versicherten.
     * Geht wie `sonstiges` an die Versicherung und ist dort das, was einen
     * Wert von einer bloßen Behauptung unterscheidet.
     */
    wertBegruendung: string;
    sonstiges: string;
    /**
     * Protokoll des automatischen Foto-Imports (Erkennung, Zählweise,
     * Wertherkunft, Unsicherheiten). Nur zur Nachvollziehbarkeit — anders als
     * `sonstiges` nicht Teil des Versicherungs-Exports.
     */
    importLog: string;
    geprueft: boolean;
    fotos: GalleryImage[];
    /** Bildausschnitt auf einem Positionsfoto, oder null wenn nicht gesetzt. */
    bildausschnitt: ItemCrop | null;
    /** Stand des Duplikat-Verdachts; "" = kein Verdacht. */
    duplikatStatus: DuplicateStatus;
    /** Auf welche Gegenstände sich der Verdacht bezieht. */
    duplikatRefs: DuplicateRef[];
}

function clamp01(n: number): number {
    return Math.min(1, Math.max(0, n));
}

/**
 * Das ACF-Feld ist ein Textfeld — der Inhalt kann also auch von Hand
 * (oder von einem Import-Skript) geschrieben worden sein. Ungültiges JSON
 * oder unvollständige Rechtecke werden zu null, statt die ganze Ansicht
 * mit einer Exception zu killen.
 */
export function parseCrop(raw: unknown): ItemCrop | null {
    if (!raw) return null;
    let data: any = raw;
    if (typeof raw === "string") {
        const text = raw.trim();
        if (!text) return null;
        try {
            data = JSON.parse(text);
        } catch {
            return null;
        }
    }
    if (!data || typeof data !== "object") return null;
    const media = Number(data.media ?? data.mediaId ?? data.ID ?? 0);
    const x = Number(data.x);
    const y = Number(data.y);
    const w = Number(data.w);
    const h = Number(data.h);
    if (!(media > 0) || ![x, y, w, h].every(Number.isFinite)) return null;
    // Ein Rechteck ohne Fläche ist kein brauchbarer Ausschnitt.
    if (!(w > 0) || !(h > 0)) return null;
    return { media, x: clamp01(x), y: clamp01(y), w: clamp01(w), h: clamp01(h) };
}

/**
 * Shop-Links liegen — wie der Bildausschnitt — als JSON in einem Textfeld.
 *
 * Damit bleibt das Schreiben über die REST-API unabhängig davon, wie ACF
 * verschachtelte Felder ausliefert (Galerie-Felder etwa kommen als rohe IDs
 * zurück, siehe resolveMedia). Der Preis ist der im Shop abgelesene Betrag,
 * nicht zwingend `wert` — der Nutzer entscheidet, welches Angebot maßgeblich
 * ist.
 *
 * Wie parseCrop toleriert die Funktion alles, was von Hand oder aus einem
 * Import-Skript im Feld gelandet sein kann: ungültiges JSON, ein einzelnes
 * Objekt statt einer Liste, ein blanker URL-String (das Format des früheren
 * Einzelfelds `shop_url`).
 */
export function parseShopLinks(raw: unknown): ShopLink[] {
    if (!raw) return [];
    let data: any = raw;
    if (typeof raw === "string") {
        const text = raw.trim();
        if (!text) return [];
        if (!text.startsWith("[") && !text.startsWith("{")) {
            // Alter Einzelwert: nur die URL, kein Preis, kein Datum.
            return [{ url: text, preis: 0, datum: "" }];
        }
        try {
            data = JSON.parse(text);
        } catch {
            return [];
        }
    }
    const list = Array.isArray(data) ? data : [data];
    return list
        .map((entry: any): ShopLink | null => {
            if (!entry) return null;
            const url = typeof entry === "string" ? entry : String(entry.url ?? "").trim();
            if (!url) return null;
            const preis = Number(typeof entry === "string" ? 0 : entry.preis);
            return {
                url,
                preis: Number.isFinite(preis) && preis > 0 ? preis : 0,
                datum: typeof entry === "string" ? "" : String(entry.datum ?? "").trim(),
            };
        })
        .filter((link): link is ShopLink => link !== null);
}

const DUPLICATE_STATUSES: DuplicateStatus[] = ["moeglich", "bestaetigt", "kein_duplikat"];

export function parseDuplicateStatus(raw: unknown): DuplicateStatus {
    const value = String(raw ?? "").trim() as DuplicateStatus;
    return DUPLICATE_STATUSES.includes(value) ? value : "";
}

/**
 * Liest die Verweisliste des Duplikat-Verdachts.
 *
 * Wie bei den Shop-Links steht im ACF-Feld eine JSON-Zeichenkette. Unlesbare
 * oder unvollständige Einträge werden verworfen statt geraten: Ein Verweis
 * ohne Ziel wäre nicht nachprüfbar und damit wertlos.
 */
export function parseDuplicateRefs(raw: unknown): DuplicateRef[] {
    if (!raw) return [];
    let data: any = raw;
    if (typeof raw === "string") {
        const text = raw.trim();
        if (!text) return [];
        try {
            data = JSON.parse(text);
        } catch {
            return [];
        }
    }
    const list = Array.isArray(data) ? data : [data];
    return list
        .map((entry: any): DuplicateRef | null => {
            if (!entry || typeof entry !== "object") return null;
            const item = Number(entry.item) || 0;
            const position = Number(entry.position) || 0;
            // Ohne eines der beiden Ziele lässt sich nichts nachschlagen.
            if (item <= 0 && position <= 0) return null;
            return { item, position, note: String(entry.note ?? "").trim() };
        })
        .filter((ref): ref is DuplicateRef => ref !== null);
}

export function serializeDuplicateRefs(refs: DuplicateRef[]): string {
    const clean = refs.filter((r) => r.item > 0 || r.position > 0);
    if (clean.length === 0) return "";
    return JSON.stringify(clean.map((r) => ({ item: r.item, position: r.position, note: r.note.trim() })));
}

export function serializeShopLinks(links: ShopLink[]): string {
    const clean = links.filter((l) => l.url.trim());
    // Leer heißt leer: ein "[]" im Feld sähe im WordPress-Backend wie ein
    // Wert aus und würde die Suche nach nicht belegten Gegenständen stören.
    if (clean.length === 0) return "";
    return JSON.stringify(
        clean.map((l) => ({
            url: l.url.trim(),
            preis: Math.round((Number(l.preis) || 0) * 100) / 100,
            datum: l.datum.trim(),
        }))
    );
}

/** Höchster belegter Shop-Preis — die Untergrenze für einen realistischen Wert. */
export function highestShopPrice(links: ShopLink[]): number {
    return links.reduce((max, l) => (l.preis > max ? l.preis : max), 0);
}

/**
 * Positionsnummer für die REST-API: leer statt `0`.
 *
 * Eine Position darf ohne Nummer existieren — beim Foto-Import ist sie nicht
 * immer bekannt und wird später nachgetragen. Ein leeres Formularfeld wird
 * jedoch zu `Number("") === 0`, und das ACF-Feld lässt nur Werte ab 1 zu.
 * Ohne diese Umwandlung würde eine leere Eingabe die Position entweder mit
 * der sinnlosen Nummer 0 speichern oder an der ACF-Validierung scheitern.
 */
/**
 * Positionsnummer für die REST-Schnittstelle.
 *
 * Ohne Nummer muss `null` gesendet werden, nicht ein leerer String: Das
 * ACF-Zahlenfeld ist als `number,null` typisiert und quittiert `""` mit
 * HTTP 400 („acf[nummer] ist nicht vom Typ number,null"). Aufgefallen ist das
 * beim Löschen eines Fotos an einer Position ohne Nummer — dort wird der
 * vollständige Datensatz zurückgeschrieben, und die leere Nummer ließ die
 * ganze Aktion scheitern.
 */
function nummerValue(nummer: number | undefined): number | null {
    return nummer && nummer > 0 ? nummer : null;
}

export function serializeCrop(crop: ItemCrop | null): string {
    if (!crop) return "";
    const r = (n: number) => Math.round(n * 10000) / 10000;
    return JSON.stringify({ media: crop.media, x: r(crop.x), y: r(crop.y), w: r(crop.w), h: r(crop.h) });
}

/**
 * Ein MCP-Zugang: ein WordPress-Application-Password samt Klartext-Passwort,
 * verwaltet über die Routen des inventory_mcp-Moduls. Aus `user`, `secret`
 * und `endpoint` baut die MCP-Ansicht die kopierbaren Start-Prompts.
 */
export interface McpCredential {
    uuid: string;
    name: string;
    /** Unix-Sekunden. */
    created: number;
    /** Unix-Sekunden oder null, wenn der Zugang noch nie benutzt wurde. */
    lastUsed: number | null;
    lastIp: string | null;
    user: string;
    secret: string;
    endpoint: string;
}

const config = () => jsidea.wp.config.hausinventar;

/** Der MCP-Endpoint der Site — auch ohne angelegten Zugang bekannt. */
export function mcpEndpoint(): string {
    return config().mcpApiUrl.replace(/\/$/, "");
}

function headers(json: boolean): Record<string, string> {
    const h: Record<string, string> = { "X-WP-Nonce": config().restNonce };
    if (json) {
        h["Content-Type"] = "application/json";
    }
    return h;
}

/**
 * Meldung über den Ladefortschritt eines Datenstroms.
 *
 * `total` ist erst ab der ersten Antwort bekannt (WordPress liefert die
 * Gesamtzahl im Header `X-WP-Total`); bis dahin steht dort 0 und die Anzeige
 * bleibt unbestimmt. `phase` unterscheidet das Laden der Datensätze vom
 * anschließenden Auflösen der Bild-IDs — beides sind eigene Rundreisen zum
 * Server und zusammen der spürbare Teil der Wartezeit.
 */
export type LoadProgress = {
    key: "rooms" | "positions" | "items" | "notes";
    label: string;
    phase: "records" | "media" | "done";
    done: number;
    total: number;
};

/* ------------------------------------------------- Laufende Vorgänge */

/**
 * Was gerade unterwegs ist: Schreibzugriffe und das Neuladen danach.
 *
 * Grundlage der Statusanzeige „Speichert …". Gezählt wird zentral hier, nicht
 * an den Aufrufstellen — jeder Schreibweg geht durch send() bzw. track(), und
 * ein neuer Knopf, der etwas speichert, ist damit automatisch abgedeckt.
 *
 * Das Neuladen zählt mit, weil es zum Speichern gehört: Bis die Daten neu
 * gelesen und gezeichnet sind, zeigt die Ansicht den alten Stand. Wer in
 * dieser Lücke weiterarbeitet, ändert einen Datensatz auf Grundlage veralteter
 * Werte — genau daraus entstehen die Race Conditions.
 */
export interface WorkState {
    /** Laufende Schreibzugriffe (Anlegen, Ändern, Löschen, Hochladen). */
    writes: number;
    /** Laufendes Neuladen des Bestands. */
    reloads: number;
}

let work: WorkState = { writes: 0, reloads: 0 };
const workListeners = new Set<(state: WorkState) => void>();

export function onWorkChange(fn: (state: WorkState) => void): void {
    workListeners.add(fn);
}

export function workState(): WorkState {
    return work;
}

/** Meldet einen Vorgang für die Dauer von `run` an — auch wenn er scheitert. */
export async function track<T>(kind: keyof WorkState, run: () => Promise<T>): Promise<T> {
    work = { ...work, [kind]: work[kind] + 1 };
    workListeners.forEach((fn) => fn(work));
    try {
        return await run();
    } finally {
        work = { ...work, [kind]: work[kind] - 1 };
        workListeners.forEach((fn) => fn(work));
    }
}

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** fetch() mit Buchführung: Schreibende Anfragen erscheinen in der Statusanzeige. */
function send(url: string, init: RequestInit = {}): Promise<Response> {
    const method = (init.method || "GET").toUpperCase();
    return WRITE_METHODS.has(method) ? track("writes", () => fetch(url, init)) : fetch(url, init);
}

type ProgressFn = (p: LoadProgress) => void;
let progressFn: ProgressFn | null = null;

/**
 * Hängt einen Beobachter an die Ladevorgänge von {@link Api.listRooms},
 * {@link Api.listPositions} und {@link Api.listItems}.
 *
 * Bewusst ein Modul-Zustand statt eines Parameters: Die drei Aufrufe stehen
 * nebenläufig in einem `Promise.all`, und der Aufrufer will einen
 * Gesamtfortschritt sehen, nicht drei getrennte.
 */
export function setLoadProgress(fn: ProgressFn | null): void {
    progressFn = fn;
}

function report(p: LoadProgress): void {
    progressFn?.(p);
}

type Stream = { key: LoadProgress["key"]; label: string };

/**
 * Führt `fn` für alle Einträge aus, höchstens `limit` gleichzeitig.
 *
 * Die Ergebnisse stehen in der Reihenfolge der Eingabe, nicht in der, in der
 * sie fertig wurden. Scheitert ein Aufruf, werden keine weiteren mehr
 * begonnen; die bereits laufenden enden noch, das Ganze wirft dann den
 * ersten Fehler.
 */
export async function inParallel<T, R>(items: T[], limit: number, fn: (item: T, index: number) => Promise<R>): Promise<R[]> {
    const results = new Array<R>(items.length);
    let next = 0;
    let failed = false;
    const worker = async () => {
        while (!failed && next < items.length) {
            const i = next++;
            try {
                results[i] = await fn(items[i], i);
            } catch (error) {
                failed = true;
                throw error;
            }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return results;
}

/**
 * Gleichzeitige Seitenabrufe je Datenstrom.
 *
 * Nacheinander brauchten die 28 Seiten Gegenstände ~4,7 s — jede Seite wartet
 * ~0,17 s auf den Server, und der hätte längst mehr gleichzeitig beantworten
 * können. Sechs zugleich überfahren PHP nicht (mehr nimmt der lokale Server
 * ohnehin nicht an, der Rest wartet kurz in der Schlange).
 */
const PAGE_CONCURRENCY = 6;

async function fetchAllPages(path: string, stream?: Stream): Promise<any[]> {
    const url = (page: number) =>
        `${config().apiUrl}${path}${path.includes("?") ? "&" : "?"}per_page=100&page=${page}&context=edit`;

    // Bleibt 0, solange der Server die Gesamtzahl nicht genannt hat.
    let total = 0;
    let loaded = 0;
    const fetchPage = async (page: number): Promise<{ batch: any[]; totalPages: number }> => {
        const response = await send(url(page), { headers: headers(false) });
        if (!response.ok) {
            // Hinter der letzten Seite antwortet WordPress mit 400 — kommt nur
            // vor, wenn zwischen erster und letzter Seite gelöscht wurde.
            if (response.status === 400 && page > 1) return { batch: [], totalPages: page - 1 };
            throw new Error(`HTTP ${response.status} bei ${path}`);
        }
        const batch = await response.json();
        if (stream) {
            // X-WP-Total ist die Zahl der Datensätze, nicht der Seiten — damit
            // lässt sich der Fortschritt in derselben Einheit zählen, in der
            // die Ergebnisse wachsen.
            total = Number(response.headers.get("X-WP-Total") || "0") || total;
            loaded += batch.length;
            report({ ...stream, phase: "records", done: loaded, total });
        }
        return { batch, totalPages: Number(response.headers.get("X-WP-TotalPages") || "1") };
    };

    // Erst die erste Seite: Sie nennt die Gesamtzahl der Seiten, danach laufen
    // alle übrigen parallel.
    const first = await fetchPage(1);
    const totalPages = first.totalPages;
    const rest = totalPages > 1 && first.batch.length > 0
        ? await inParallel(
            Array.from({ length: totalPages - 1 }, (_, i) => i + 2),
            PAGE_CONCURRENCY,
            fetchPage,
        )
        : [];
    return [first, ...rest].flatMap((r) => r.batch);
}

/**
 * Wirft mit der WP-REST-Fehlermeldung, statt eine Error-Antwort stillschweigend
 * als gültiges Objekt zu behandeln (sonst sieht ein fehlgeschlagenes Speichern
 * für den Aufrufer wie ein Erfolg mit leeren Feldern aus).
 */
async function parseResponse(response: Response): Promise<any> {
    const json = await response.json();
    if (!response.ok) {
        throw new Error(json?.message || `HTTP ${response.status}`);
    }
    return json;
}

/**
 * Media-Map für die Antwort eines Schreibzugriffs.
 *
 * Die REST-Antwort nennt Fotos nur als IDs. Die App hat sie meist schon
 * aufgelöst (sie schickt sie ja mit) — die werden übernommen, und nur
 * wirklich neue IDs kosten eine Anfrage. So kann die App den gespeicherten
 * Datensatz direkt übernehmen, statt den ganzen Bestand neu zu laden.
 */
async function mediaFor(raw: any, known: GalleryImage[] = []): Promise<Map<number, GalleryImage>> {
    const map = new Map(known.map((f) => [f.ID, f]));
    const fehlend = fotoIds(raw?.acf || {}).filter((id) => !map.has(id));
    if (fehlend.length > 0) {
        for (const [id, foto] of await resolveMedia(fehlend)) map.set(id, foto);
    }
    return map;
}

/**
 * Wirft, wenn ein Löschen nicht durchging.
 *
 * Früher egal, weil danach ohnehin alles neu geladen wurde und der Datensatz
 * dann eben wieder dastand. Seit die App ihren Stand selbst nachführt, würde
 * er sonst verschwinden, obwohl er auf dem Server noch existiert.
 */
async function expectDeleted(response: Response): Promise<void> {
    if (response.ok) return;
    let message = `HTTP ${response.status}`;
    try {
        message = (await response.json())?.message || message;
    } catch {
        // Kein JSON in der Fehlerantwort — beim Statuscode bleiben.
    }
    throw new Error(message);
}

function fotoIds(acf: any): number[] {
    return Array.isArray(acf.fotos) ? acf.fotos.map((id: unknown) => Number(id)).filter((id: number) => id > 0) : [];
}

function resolveFotos(acf: any, mediaMap: Map<number, GalleryImage>): GalleryImage[] {
    return fotoIds(acf)
        .map((id) => mediaMap.get(id))
        .filter((img): img is GalleryImage => !!img);
}

/**
 * Größte Anzahl Anhänge, die `/wp/v2/media` in einer Antwort liefert.
 * WordPress deckelt `per_page` serverseitig bei 100 — ein höherer Wert wird
 * nicht etwa gekürzt, sondern mit einem 400er abgelehnt.
 */
const MEDIA_PAGE_SIZE = 100;

/**
 * ACF liefert Galerie-Felder über die REST-API nur als rohe Anhang-IDs
 * (nicht als formatierte Objekte wie get_field() es im Template tut) —
 * daher werden die Bild-URLs separat über /wp/v2/media aufgelöst.
 *
 * Die IDs werden dabei in Hunderterblöcke zerlegt. Ohne diese Aufteilung
 * liefert WordPress nur die ersten 100 Anhänge, und jede Position, deren Foto
 * nicht darunter ist, erscheint in der App ohne Bild — obwohl das Foto im
 * Backend sauber verknüpft ist. Der Fehler zeigt sich erst ab dem 101. Foto im
 * gesamten Bestand und trifft dann ausgerechnet die ältesten Positionen, weil
 * `include` nach Datum absteigend sortiert.
 */
async function resolveMedia(ids: number[]): Promise<Map<number, GalleryImage>> {
    const unique = Array.from(new Set(ids)).filter((id) => id > 0);
    const map = new Map<number, GalleryImage>();
    if (unique.length === 0) {
        return map;
    }

    const chunks: number[][] = [];
    for (let i = 0; i < unique.length; i += MEDIA_PAGE_SIZE) {
        chunks.push(unique.slice(i, i + MEDIA_PAGE_SIZE));
    }

    const responses = await Promise.all(
        chunks.map((chunk) =>
            send(`${config().apiUrl}media?include=${chunk.join(",")}&per_page=${MEDIA_PAGE_SIZE}`, {
                headers: headers(false),
            })
        )
    );

    for (const response of responses) {
        if (!response.ok) {
            continue;
        }
        const items = await response.json();
        for (const m of items) {
            map.set(m.id, {
                ID: m.id,
                url: m.source_url,
                filename: m.title?.raw || m.slug || "",
                width: Number(m.media_details?.width) || undefined,
                height: Number(m.media_details?.height) || undefined,
                sizes: {
                    thumbnail: m.media_details?.sizes?.thumbnail?.source_url,
                    medium: m.media_details?.sizes?.medium?.source_url,
                    large: m.media_details?.sizes?.large?.source_url,
                },
            });
        }
    }
    return map;
}

function toRoom(raw: any, mediaMap: Map<number, GalleryImage> = new Map()): Room {
    const acf = raw.acf || {};
    return {
        id: raw.id,
        title: raw.title?.raw ?? raw.title?.rendered ?? "",
        order: Number(raw.menu_order) || 0,
        beschreibung: acf.beschreibung || "",
        fotos: resolveFotos(acf, mediaMap),
    };
}

const NOTE_LEVELS: NoteLevel[] = ["kritisch", "warnung", "hinweis"];

/**
 * ACF-Nutzlast eines Hinweises — geteilt zwischen Anlegen und Ändern.
 *
 * Die drei Verweise werden bei 0 als `null` gesendet: Ein post_object-Feld
 * lässt sich nur so leeren, eine 0 würde als Post-ID gelesen.
 */
function noteFields(data: Partial<Note>): Record<string, unknown> {
    const acf: Record<string, unknown> = {};
    if (data.level !== undefined) acf.wichtigkeit = data.level;
    if (data.text !== undefined) acf.text = data.text;
    if (data.erledigt !== undefined) acf.erledigt = data.erledigt;
    if (data.imageKey !== undefined) acf.image_key = data.imageKey;
    if (data.raum !== undefined) acf.raum = data.raum || null;
    if (data.position !== undefined) acf.position = data.position || null;
    if (data.item !== undefined) acf.item = data.item || null;
    return acf;
}

function toNote(raw: any): Note {
    const acf = raw.acf || {};
    const level = String(acf.wichtigkeit || "");
    return {
        id: raw.id,
        title: raw.title?.raw ?? raw.title?.rendered ?? "",
        order: Number(raw.menu_order) || 0,
        // Unbekannter oder leerer Wert gilt als "hinweis" — ein Hinweis ohne
        // Stufe soll sichtbar bleiben, nur eben nicht als kritisch.
        level: (NOTE_LEVELS as string[]).includes(level) ? (level as NoteLevel) : "hinweis",
        text: acf.text || "",
        erledigt: !!acf.erledigt,
        imageKey: acf.image_key || "",
        raum: Number(acf.raum) || 0,
        position: Number(acf.position) || 0,
        item: Number(acf.item) || 0,
    };
}

function toPosition(raw: any, mediaMap: Map<number, GalleryImage> = new Map()): Position {
    const acf = raw.acf || {};
    return {
        id: raw.id,
        title: raw.title?.raw ?? raw.title?.rendered ?? "",
        nummer: Number(acf.nummer) || 0,
        raum: Number(acf.raum) || 0,
        beschreibung: acf.beschreibung || "",
        imageKey: acf.image_key || "",
        importLog: acf.import_log || "",
        fotos: resolveFotos(acf, mediaMap),
    };
}

function toItem(raw: any, mediaMap: Map<number, GalleryImage> = new Map()): Item {
    const acf = raw.acf || {};
    return {
        id: raw.id,
        title: raw.title?.raw ?? raw.title?.rendered ?? "",
        order: Number(raw.menu_order) || 0,
        position: Number(acf.position) || 0,
        anzahl: Number(acf.anzahl) || 0,
        alter: acf.alter || "",
        wert: Number(acf.wert) || 0,
        versandkosten: Number(acf.versandkosten) || 0,
        datum: acf.datum || "",
        quelle: acf.quelle || "",
        shopLinks: parseShopLinks(acf.shop_links),
        wertBegruendung: acf.wert_begruendung || "",
        sonstiges: acf.sonstiges || "",
        importLog: acf.import_log || "",
        geprueft: !!acf.geprueft,
        fotos: resolveFotos(acf, mediaMap),
        bildausschnitt: parseCrop(acf.bildausschnitt),
        duplikatStatus: parseDuplicateStatus(acf.duplikat_status),
        duplikatRefs: parseDuplicateRefs(acf.duplikat_refs),
    };
}

export const Api = {
    async listRooms(): Promise<Room[]> {
        const stream: Stream = { key: "rooms", label: "Räume" };
        const raw = await fetchAllPages("hausinventar_room", stream);
        report({ ...stream, phase: "media", done: raw.length, total: raw.length });
        const mediaMap = await resolveMedia(raw.flatMap((r) => fotoIds(r.acf || {})));
        report({ ...stream, phase: "done", done: raw.length, total: raw.length });
        return raw.map((r) => toRoom(r, mediaMap));
    },

    async listPositions(): Promise<Position[]> {
        const stream: Stream = { key: "positions", label: "Positionen" };
        const raw = await fetchAllPages("hausinventar_pos", stream);
        report({ ...stream, phase: "media", done: raw.length, total: raw.length });
        const mediaMap = await resolveMedia(raw.flatMap((p) => fotoIds(p.acf || {})));
        report({ ...stream, phase: "done", done: raw.length, total: raw.length });
        return raw.map((p) => toPosition(p, mediaMap));
    },

    async listItems(): Promise<Item[]> {
        const stream: Stream = { key: "items", label: "Gegenstände" };
        const raw = await fetchAllPages("hausinventar_item", stream);
        report({ ...stream, phase: "media", done: raw.length, total: raw.length });
        const mediaMap = await resolveMedia(raw.flatMap((i) => fotoIds(i.acf || {})));
        report({ ...stream, phase: "done", done: raw.length, total: raw.length });
        return raw.map((i) => toItem(i, mediaMap));
    },

    async listNotes(): Promise<Note[]> {
        const stream: Stream = { key: "notes", label: "Import-Hinweise" };
        const raw = await fetchAllPages("hausinventar_note", stream);
        report({ ...stream, phase: "done", done: raw.length, total: raw.length });
        return raw.map(toNote);
    },

    async createNote(data: Partial<Note>): Promise<Note> {
        const response = await send(`${config().apiUrl}hausinventar_note`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({
                title: data.title || "Hinweis",
                status: "publish",
                menu_order: data.order,
                acf: noteFields(data),
            }),
        });
        return toNote(await parseResponse(response));
    },

    async updateNote(id: number, data: Partial<Note>): Promise<Note> {
        const body: Record<string, unknown> = { acf: noteFields(data) };
        if (data.title !== undefined) body.title = data.title;
        if (data.order !== undefined) body.menu_order = data.order;

        const response = await send(`${config().apiUrl}hausinventar_note/${id}`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify(body),
        });
        return toNote(await parseResponse(response));
    },

    async deleteNote(id: number): Promise<void> {
        await expectDeleted(await send(`${config().apiUrl}hausinventar_note/${id}?force=true`, {
            method: "DELETE",
            headers: headers(false),
        }));
    },

    async createRoom(data: Partial<Room>): Promise<Room> {
        const response = await send(`${config().apiUrl}hausinventar_room`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({
                title: data.title,
                status: "publish",
                menu_order: data.order,
                acf: {
                    beschreibung: data.beschreibung,
                },
            }),
        });
        const raw = await parseResponse(response);
        return toRoom(raw, await mediaFor(raw, data.fotos));
    },

    /** `known`: bereits aufgelöste Fotos, falls `data` keine mitbringt (spart die Medien-Anfrage). */
    async updateRoom(id: number, data: Partial<Room>, known?: GalleryImage[]): Promise<Room> {
        const acf: Record<string, unknown> = {};
        if (data.beschreibung !== undefined) acf.beschreibung = data.beschreibung;
        if (data.fotos !== undefined) acf.fotos = data.fotos.map((f) => f.ID);

        const body: Record<string, unknown> = { acf };
        if (data.title !== undefined) body.title = data.title;
        if (data.order !== undefined) body.menu_order = data.order;

        const response = await send(`${config().apiUrl}hausinventar_room/${id}`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify(body),
        });
        const raw = await parseResponse(response);
        return toRoom(raw, await mediaFor(raw, data.fotos ?? known));
    },

    async deleteRoom(id: number): Promise<void> {
        await expectDeleted(await send(`${config().apiUrl}hausinventar_room/${id}?force=true`, {
            method: "DELETE",
            headers: headers(false),
        }));
    },

    /** Persistiert eine per Drag & Drop geänderte Reihenfolge (menu_order) mehrerer Räume. */
    async reorderRooms(pairs: { id: number; order: number }[]): Promise<void> {
        const results = await Promise.all(
            pairs.map(({ id, order }) =>
                send(`${config().apiUrl}hausinventar_room/${id}`, {
                    method: "POST",
                    headers: headers(true),
                    body: JSON.stringify({ menu_order: order }),
                })
            )
        );
        const failed = results.find((r) => !r.ok);
        if (failed) {
            throw new Error(`HTTP ${failed.status} beim Speichern der Reihenfolge`);
        }
    },

    async createPosition(data: Partial<Position>): Promise<Position> {
        const acf: Record<string, unknown> = {
            nummer: nummerValue(data.nummer),
            raum: data.raum,
            beschreibung: data.beschreibung,
        };
        if (data.importLog !== undefined) {
            acf.import_log = data.importLog;
        }
        if (data.fotos !== undefined) {
            acf.fotos = data.fotos.map((f) => f.ID);
        }

        const response = await send(`${config().apiUrl}hausinventar_pos`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({
                title: data.title,
                status: "publish",
                acf,
            }),
        });
        const raw = await parseResponse(response);
        return toPosition(raw, await mediaFor(raw, data.fotos));
    },

    async updatePosition(id: number, data: Partial<Position>, known?: GalleryImage[]): Promise<Position> {
        const acf: Record<string, unknown> = {};
        if (data.nummer !== undefined) acf.nummer = nummerValue(data.nummer);
        if (data.raum !== undefined) acf.raum = data.raum;
        if (data.beschreibung !== undefined) acf.beschreibung = data.beschreibung;
        if (data.importLog !== undefined) acf.import_log = data.importLog;
        if (data.fotos !== undefined) acf.fotos = data.fotos.map((f) => f.ID);

        const body: Record<string, unknown> = { acf };
        if (data.title !== undefined) body.title = data.title;

        const response = await send(`${config().apiUrl}hausinventar_pos/${id}`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify(body),
        });
        const raw = await parseResponse(response);
        return toPosition(raw, await mediaFor(raw, data.fotos ?? known));
    },

    async deletePosition(id: number): Promise<void> {
        await expectDeleted(await send(`${config().apiUrl}hausinventar_pos/${id}?force=true`, {
            method: "DELETE",
            headers: headers(false),
        }));
    },

    /**
     * Entfernt ein Foto endgültig aus der Mediathek.
     *
     * Anhänge kennen keinen Papierkorb — ohne force=true lehnt die REST-API
     * das Löschen ab. Beim Löschen eines Beitrags räumt WordPress die daran
     * hängenden Dateien nicht mit weg, sie müssen also einzeln entfernt
     * werden.
     */
    async deleteMedia(ids: number[]): Promise<void> {
        await Promise.all(
            ids.map((id) =>
                send(`${config().apiUrl}media/${id}?force=true`, {
                    method: "DELETE",
                    headers: headers(false),
                })
            )
        );
    },

    async createItem(data: Partial<Item>): Promise<Item> {
        const acf: Record<string, unknown> = {
            position: data.position,
            anzahl: data.anzahl,
            alter: data.alter,
            wert: data.wert,
            versandkosten: data.versandkosten,
            datum: data.datum,
            sonstiges: data.sonstiges,
            wert_begruendung: data.wertBegruendung,
            shop_links: serializeShopLinks(data.shopLinks ?? []),
            geprueft: data.geprueft || false,
            bildausschnitt: serializeCrop(data.bildausschnitt ?? null),
            duplikat_refs: serializeDuplicateRefs(data.duplikatRefs ?? []),
        };
        // Auswahlfeld: wie "quelle" akzeptiert die REST-Validierung keinen
        // leeren String, nur eine der hinterlegten Optionen.
        if (data.duplikatStatus) {
            acf.duplikat_status = data.duplikatStatus;
        }
        if (data.importLog !== undefined) {
            acf.import_log = data.importLog;
        }
        // "quelle" ist ein ACF-Auswahlfeld — die REST-Validierung akzeptiert
        // nur die hinterlegten Optionen, kein leerer String. Ohne Auswahl
        // wird das Feld einfach ausgelassen.
        if (data.quelle) {
            acf.quelle = data.quelle;
        }
        if (data.fotos !== undefined) {
            acf.fotos = data.fotos.map((f) => f.ID);
        }

        const response = await send(`${config().apiUrl}hausinventar_item`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({
                title: data.title || "Gegenstand",
                status: "publish",
                menu_order: data.order,
                acf,
            }),
        });
        const raw = await parseResponse(response);
        return toItem(raw, await mediaFor(raw, data.fotos));
    },

    async updateItem(id: number, data: Partial<Item>, known?: GalleryImage[]): Promise<Item> {
        const acf: Record<string, unknown> = {};
        for (const key of ["position", "anzahl", "alter", "wert", "versandkosten", "datum", "sonstiges", "geprueft"] as const) {
            if (data[key] !== undefined) {
                acf[key] = data[key];
            }
        }
        if (data.importLog !== undefined) {
            acf.import_log = data.importLog;
        }
        if (data.shopLinks !== undefined) {
            acf.shop_links = serializeShopLinks(data.shopLinks);
        }
        if (data.wertBegruendung !== undefined) {
            acf.wert_begruendung = data.wertBegruendung;
        }
        if (data.duplikatRefs !== undefined) {
            acf.duplikat_refs = serializeDuplicateRefs(data.duplikatRefs);
        }
        // Anders als bei "quelle" muss hier auch das Zurücksetzen möglich sein:
        // "kein Verdacht" ist ein Zustand, den der Nutzer aktiv wählt. Gelöscht
        // wird ein Auswahlfeld allerdings nur mit null — ein leerer String gilt
        // der REST-Validierung als ungültige Option und quittiert mit 400.
        if (data.duplikatStatus !== undefined) {
            acf.duplikat_status = data.duplikatStatus || null;
        }
        if (data.quelle) {
            acf.quelle = data.quelle;
        }
        if (data.fotos !== undefined) acf.fotos = data.fotos.map((f) => f.ID);
        // null bedeutet hier "Ausschnitt entfernen" und muss als leerer String
        // durchgereicht werden — nur undefined lässt das Feld unangetastet.
        if (data.bildausschnitt !== undefined) acf.bildausschnitt = serializeCrop(data.bildausschnitt);

        const body: Record<string, unknown> = { acf };
        if (data.title !== undefined) body.title = data.title;
        if (data.order !== undefined) body.menu_order = data.order;

        const response = await send(`${config().apiUrl}hausinventar_item/${id}`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify(body),
        });
        const raw = await parseResponse(response);
        return toItem(raw, await mediaFor(raw, data.fotos ?? known));
    },

    async deleteItem(id: number): Promise<void> {
        await expectDeleted(await send(`${config().apiUrl}hausinventar_item/${id}?force=true`, {
            method: "DELETE",
            headers: headers(false),
        }));
    },

    /** Persistiert eine per Drag & Drop geänderte Reihenfolge (menu_order) mehrerer Gegenstände. */
    async reorderItems(pairs: { id: number; order: number }[]): Promise<void> {
        const results = await Promise.all(
            pairs.map(({ id, order }) =>
                send(`${config().apiUrl}hausinventar_item/${id}`, {
                    method: "POST",
                    headers: headers(true),
                    body: JSON.stringify({ menu_order: order }),
                })
            )
        );
        const failed = results.find((r) => !r.ok);
        if (failed) {
            throw new Error(`HTTP ${failed.status} beim Speichern der Reihenfolge`);
        }
    },

    /**
     * fetch() bietet keinen zuverlässigen Upload-Fortschritt über alle
     * Browser hinweg — daher XMLHttpRequest, dessen "upload.onprogress"
     * die tatsächlich gesendeten Bytes liefert.
     */
    uploadMedia(file: File, postId?: number | null, onProgress?: (loadedBytes: number) => void): Promise<GalleryImage> {
        return track("writes", () => new Promise<GalleryImage>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${config().apiUrl}media`);
            xhr.setRequestHeader("X-WP-Nonce", config().restNonce);
            xhr.upload.addEventListener("progress", (e) => {
                if (onProgress) onProgress(e.loaded);
            });
            xhr.addEventListener("load", () => {
                if (xhr.status < 200 || xhr.status >= 300) {
                    reject(new Error(`Upload fehlgeschlagen (${xhr.status})`));
                    return;
                }
                const data = JSON.parse(xhr.responseText);
                resolve({
                    ID: data.id,
                    url: data.source_url,
                    filename: data.title?.raw || file.name,
                    sizes: {
                        thumbnail: data.media_details?.sizes?.thumbnail?.source_url,
                        medium: data.media_details?.sizes?.medium?.source_url,
                    },
                });
            });
            xhr.addEventListener("error", () => reject(new Error("Upload fehlgeschlagen")));
            const form = new FormData();
            form.append("file", file, file.name);
            // "post" ist das WP-Feld hinter "Hochgeladen zu" in der Mediathek.
            if (postId && postId > 0) {
                form.append("post", String(postId));
            }
            xhr.send(form);
        }));
    },

    /**
     * Trägt "Hochgeladen zu" nach. Nötig für Fotos, die schon vor dem
     * Anlegen des Beitrags hochgeladen wurden (neue Position / neuer
     * Gegenstand) — dort ist die Post-ID zum Upload-Zeitpunkt noch unbekannt.
     * Fehler werden bewusst geschluckt: die Zuordnung ist Komfort, ihr
     * Fehlschlagen darf das bereits gespeicherte Objekt nicht kippen.
     */
    async attachMediaToPost(mediaIds: number[], postId: number): Promise<void> {
        if (!postId || mediaIds.length === 0) return;
        await Promise.all(
            mediaIds.map(async (id): Promise<void> => {
                try {
                    await send(`${config().apiUrl}media/${id}`, {
                        method: "POST",
                        headers: headers(true),
                        body: JSON.stringify({ post: postId }),
                    });
                } catch {
                    // Absichtlich ignoriert — siehe Kommentar oben.
                }
            })
        );
    },

    async listMcpCredentials(): Promise<McpCredential[]> {
        const response = await send(`${config().mcpApiUrl}credentials`, { headers: headers(false) });
        const json = await parseResponse(response);
        return Array.isArray(json?.credentials) ? json.credentials : [];
    },

    async createMcpCredential(name: string): Promise<McpCredential> {
        const response = await send(`${config().mcpApiUrl}credentials`, {
            method: "POST",
            headers: headers(true),
            body: JSON.stringify({ name }),
        });
        return parseResponse(response);
    },

    async deleteMcpCredential(uuid: string): Promise<void> {
        const response = await send(`${config().mcpApiUrl}credentials/${encodeURIComponent(uuid)}`, {
            method: "DELETE",
            headers: headers(false),
        });
        await parseResponse(response);
    },
};

/** Zugang zu einer anderen Instanz, gegen die verglichen wird. */
export interface ReferenceConfig {
    /** Adresse der Instanz, z. B. "https://hausinventar.example.de". */
    url: string;
    /** Benutzername dort (nicht die E-Mail-Adresse). */
    user: string;
    /** Anwendungspasswort dieses Benutzers. */
    secret: string;
}

/** Route der Vergleichs-Schnittstelle, siehe Modul inventory_compare. */
const COMPARE_ROUTE = "/wp-json/jsidea/v1/compare/snapshot";

/**
 * Holt den Datenstand einer anderen Instanz.
 *
 * Der einzige Aufruf im Projekt, der an einen **fremden** Host geht — alles
 * andere spricht die eigene Site über `config().apiUrl` an. Deshalb hier auch
 * der einzige Ort mit einem Authorization-Header: Angemeldet wird mit einem
 * Anwendungspasswort der Gegenseite, nicht mit dem eigenen Cookie.
 *
 * `credentials: "omit"` ist wichtig und nicht nur Vorsicht: Mit Cookies wäre
 * es eine Anfrage "mit Anmeldedaten", für die der Browser strengere
 * CORS-Regeln anlegt — und Cookies der Gegenseite hat dieser Browser ohnehin
 * nur, wenn dort zufällig jemand angemeldet ist. Genau das soll das Ergebnis
 * nicht beeinflussen.
 *
 * Die Fehler werden übersetzt, weil die Ursachen hier weit auseinanderliegen
 * und der Browser sie alle gleich meldet ("Failed to fetch").
 */
export async function fetchReferenceSnapshot(cfg: ReferenceConfig): Promise<Snapshot> {
    const basis = cfg.url.trim().replace(/\/+$/, "");
    if (!/^https?:\/\//i.test(basis)) {
        throw new Error("Die Adresse muss mit http:// oder https:// beginnen.");
    }

    let response: Response;
    try {
        response = await send(basis + COMPARE_ROUTE, {
            credentials: "omit",
            headers: {
                Authorization: "Basic " + btoa(`${cfg.user}:${cfg.secret}`),
                Accept: "application/json",
            },
        });
    } catch {
        // Der Browser nennt den Grund nicht — die drei, die hier vorkommen,
        // sind: Seite nicht erreichbar, gemischte Inhalte, Zertifikat.
        const gemischt = location.protocol === "https:" && basis.toLowerCase().startsWith("http://");
        throw new Error(gemischt
            ? `Diese Seite läuft über https, die Referenz über http — der Browser blockiert solche Abrufe. Rufen Sie den Vergleich von der http-Instanz aus auf oder geben Sie die Referenz mit https an.`
            : `${basis} hat nicht geantwortet. Entweder ist die Adresse falsch oder dort läuft kein WordPress mit diesem Inventar; bei einer lokalen Instanz muss sie im selben Browser aufrufbar sein und ihr Zertifikat anerkannt.`);
    }

    if (response.status === 401 || response.status === 403) {
        throw new Error("Benutzername oder Anwendungspasswort wurden nicht anerkannt. Das Anwendungspasswort legen Sie auf der Referenz im WordPress-Profil an.");
    }
    if (response.status === 404) {
        throw new Error("Unter dieser Adresse antwortet die Vergleichs-Schnittstelle nicht. Die Referenz braucht dieselbe Theme-Fassung wie diese Instanz.");
    }
    if (!response.ok) {
        throw new Error(`Die Referenz antwortete mit HTTP ${response.status}.`);
    }

    const snapshot = await response.json();
    if (!snapshot || !Array.isArray(snapshot.gegenstaende) || !Array.isArray(snapshot.positionen)) {
        throw new Error("Die Antwort der Referenz war kein Datenstand. Zeigt die Adresse wirklich auf ein Hausinventar?");
    }
    return snapshot as Snapshot;
}

/**
 * Löscht den gesamten Bestand: Gegenstände, Positionen, Räume,
 * Import-Hinweise und die daran hängenden Fotos.
 *
 * Nur für den Entwicklungsrechner gedacht — den Weg dorthin gibt ausschließlich
 * `canReset` frei (siehe InventoryApp::mayResetEverything). Serverseitig prüft
 * die REST-API jede einzelne Anfrage weiterhin gegen die Fähigkeiten des
 * angemeldeten Zugangs.
 *
 * Steht bewusst **neben** dem Api-Objekt statt darin: Eine Methode, die über
 * `this` andere Methoden desselben Objektliterals aufruft, macht dessen
 * Typableitung zirkulär (TS7011). Hier ist der Typ von `Api` bereits vollständig
 * aufgelöst.
 *
 * Reihenfolge von innen nach außen: erst die Gegenstände, dann ihre Positionen,
 * dann die Räume. Andersherum blieben Kinder mit einem Verweis ins Leere
 * stehen, falls der Vorgang mittendrin abbricht — und genau die wären danach
 * von Hand kaum wiederzufinden.
 *
 * Die Fotos werden **vor** dem ersten Löschen eingesammelt: Sind die Beiträge
 * erst weg, ist auch die Zuordnung weg und die Dateien blieben als verwaiste
 * Mediathek-Einträge zurück.
 */
export async function deleteEverything(
    onProgress?: (done: number, total: number, label: string) => void,
): Promise<void> {
    const [rooms, positions, items, notes] = await Promise.all([
        Api.listRooms(),
        Api.listPositions(),
        Api.listItems(),
        Api.listNotes(),
    ]);

    const mediaIds = new Set<number>();
    for (const owner of [...rooms, ...positions, ...items]) {
        for (const foto of owner.fotos ?? []) mediaIds.add(foto.ID);
    }

    type Stufe = { label: string; ids: number[]; remove: (id: number) => Promise<void> };
    const stufen: Stufe[] = [
        { label: "Gegenstände", ids: items.map((i) => i.id), remove: (id: number) => Api.deleteItem(id) },
        { label: "Positionen", ids: positions.map((p) => p.id), remove: (id: number) => Api.deletePosition(id) },
        { label: "Räume", ids: rooms.map((r) => r.id), remove: (id: number) => Api.deleteRoom(id) },
        { label: "Import-Hinweise", ids: notes.map((n) => n.id), remove: (id: number) => Api.deleteNote(id) },
        { label: "Fotos", ids: [...mediaIds], remove: (id: number) => Api.deleteMedia([id]) },
    ];

    /**
     * Einzelne Fehlschläge stoppen den Lauf nicht: Ein Foto, das schon weg ist,
     * darf die restlichen neunhundert Löschungen nicht verhindern.
     */
    const removeQuietly = async (stufe: Stufe, id: number): Promise<void> => {
        try {
            await stufe.remove(id);
        } catch {
            /* bewusst geschluckt */
        }
    };

    const total = stufen.reduce((summe, stufe) => summe + stufe.ids.length, 0);
    let done = 0;
    onProgress?.(0, total, "Vorbereiten");

    for (const stufe of stufen) {
        // In Wellen statt alle auf einmal: Tausend gleichzeitige Anfragen
        // bringen den lokalen PHP-Server ins Schwitzen, und der Browser
        // drosselt sie ohnehin auf wenige Verbindungen pro Host.
        const WELLE = 12;
        for (let i = 0; i < stufe.ids.length; i += WELLE) {
            await Promise.all(stufe.ids.slice(i, i + WELLE).map((id) => removeQuietly(stufe, id)));
            done += Math.min(WELLE, stufe.ids.length - i);
            onProgress?.(done, total, stufe.label);
        }
    }
}
